import { useState } from 'react';
import type { EffectivePayday, LocalDate } from '@tahi/core';
import { Button } from '../../../ui';
import { TextField } from '../../../ui/TextField';
import { copy } from '../copy';
import { AccountChips } from './AccountChips';
import { dayShort, minorToInput, parsePesoInput } from '../format';

/** The sheet title: brand.md "Payday. Did ₱18,000 come in?" */
export function paydayTitle(p: EffectivePayday, today: LocalDate): string {
  if (p.status === 'received') return copy.receivedTitle(p.amountMinor ?? 0);
  if (p.status === 'skipped') return copy.skippedTitle;
  if (p.date > today) return copy.expectedOn(p.defaultAmountMinor, dayShort(p.date));
  return copy.paydayQuestion(p.defaultAmountMinor);
}

export interface PaydayConfirmProps {
  payday: EffectivePayday;
  today: LocalDate;
  /** Bonuses only (spec). */
  canSkip: boolean;
  error?: string | null;
  onConfirm(amountMinor: number, date: LocalDate, accountId?: string | null): void | Promise<void>;
  /** v0.5: accounts to land it in, and the one used when none is picked. */
  accounts?: { id: string; name: string }[];
  defaultAccountId?: string | null;
  onSkip(): void | Promise<void>;
  onUndo(): void | Promise<void>;
  onEditReceived(amountMinor: number, date: LocalDate): void | Promise<void>;
  onClose(): void;
}

export function PaydayConfirm({
  payday: p,
  today,
  canSkip,
  error = null,
  onConfirm,
  onSkip,
  onUndo,
  onEditReceived,
  accounts = [],
  defaultAccountId = null,
  onClose,
}: PaydayConfirmProps) {
  const early = p.status === 'expected' && p.date > today;
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(minorToInput(p.amountMinor ?? p.defaultAmountMinor));
  const [date, setDate] = useState<LocalDate>(early ? today : p.date);
  const [busy, setBusy] = useState(false);
  const [accountId, setAccountId] = useState<string | null>(defaultAccountId);
  const minor = parsePesoInput(amount);

  async function run(fn: () => void | Promise<void>) {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  }

  const subtitle = [
    p.sourceName,
    dayShort(p.date),
    p.status === 'received' && p.amountMinor !== p.defaultAmountMinor
      ? copy.expectedWas(p.defaultAmountMinor)
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  function save() {
    if (minor === null) return;
    void run(() =>
      p.status === 'received' ? onEditReceived(minor, date) : onConfirm(minor, date, accountId),
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted">{subtitle}</p>
      {p.status === 'skipped' ? <p className="text-sm text-muted">{copy.skippedNote}</p> : null}
      {p.status === 'expected' && accounts.length > 0 ? (
        <AccountChips
          label={copy.landsIn}
          accounts={accounts}
          value={accountId}
          onChange={setAccountId}
        />
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {editing ? (
        <div className="flex flex-col gap-3">
          <TextField
            label={copy.amount}
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <TextField
            label={copy.dayItCame}
            type="date"
            value={date}
            max={today}
            onChange={(e) => e.target.value && setDate(e.target.value)}
          />
          <Button variant="primary" block disabled={minor === null || busy} onClick={save}>
            {copy.save}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {p.status === 'expected' && !early ? (
            <Button
              variant="primary"
              block
              disabled={busy}
              onClick={() => void run(() => onConfirm(p.defaultAmountMinor, p.date, accountId))}
            >
              {copy.confirm}
            </Button>
          ) : null}
          {p.status === 'expected' ? (
            <Button
              variant={early ? 'primary' : 'secondary'}
              block
              onClick={() => setEditing(true)}
            >
              {early ? copy.cameAlready : copy.differentAmount}
            </Button>
          ) : null}
          {p.status === 'expected' && canSkip ? (
            <Button variant="ghost" block disabled={busy} onClick={() => void run(onSkip)}>
              {copy.didntCome}
            </Button>
          ) : null}
          {p.status === 'received' ? (
            <Button variant="secondary" block onClick={() => setEditing(true)}>
              {copy.changeAmount}
            </Button>
          ) : null}
          {p.status !== 'expected' ? (
            <Button variant="ghost" block disabled={busy} onClick={() => void run(onUndo)}>
              {p.status === 'skipped' ? copy.undoSkip : copy.undo}
            </Button>
          ) : null}
          {p.status === 'expected' ? (
            <Button variant="ghost" block onClick={onClose}>
              {copy.notYet}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}
